import React, { useEffect, useMemo, useRef, useId, useState } from "react";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { AscDesc, BidOptions, OrderOptions } from "./OrderReport";
import { Checkbox, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup } from "@mui/material";

type props = {
    onClose: () => void;
    onOrderSort: (
        sortOrderBy: OrderOptions,
        sortDirection: AscDesc,
        filter: string [],
    ) => void;
    onBidSort: (
        sortBidBy: BidOptions,
        sortDirection: AscDesc,
    ) => void;
    visible: boolean;
    activeTab: 'Tab1' | 'Tab2';
    filteredItems: string [];
};

type FilterOptions = [] 
    | 'Complete' 
    | 'Delivered'
    | 'In Process'
    | 'Ready For Pickup'
    | 'Out For Delivery'
    | 'Pending'
    | 'Order Being Picked'
    | 'Contact Hirsch'
    | 'Late'
    | null;

const filterOptions = [
    'Pending',
    'In Process',
    'Order Being Picked',
    'Delivered',
    'Ready For Pickup',
    'Out For Delivery',
    'Late',
    'Contact Hirsch',
]

const SortModal = ({ onClose, onOrderSort, onBidSort, visible, activeTab, filteredItems }: props) => {
    const id = useId();
    const [sortOrderBy, setSortOrderBy] = useState<OrderOptions>(null);
    const [sortBidBy, setSortBidBy] = useState<BidOptions>(null);
    const [sortDirection, setSortDirection] = useState<AscDesc>(null);
    const [filter, setFilter] = useState<string []>([]);
    
    useEffect(() => {
        if (filteredItems?.length === 0) {
            setFilter([]);
        }
    }, [filteredItems]);

    const handleClose = () => {
        onClose();
    };

    const handleChange = (
        _: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ) => {
        if (activeTab === 'Tab1') {
            setSortOrderBy(value as OrderOptions)
        } else {
            setSortBidBy(value as BidOptions)
        }
    };

    const handleDirectionChange = (
        _: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ) => {
        setSortDirection(value as AscDesc);
    };

    const handleSubmit = () => {
        if (activeTab === 'Tab1') {
            onOrderSort(sortOrderBy, sortDirection, filter);
        } else {
            onBidSort(sortBidBy, sortDirection);
        }

        onClose();
    };

    return (
        <Modal
            isOpen={visible}
            onClosed={handleClose}
        >
            <ModalHeader
                className='bg-primary d-flex justify-content-center'
                style={{ color: 'white', }}
            >
                <Button 
                    title='Close'
                    onClick={onClose}
                    className="bg-transparent border-0 fw-bold text-white position-absolute start-0 ms-2"
                    unmountOnClose
                >
                    Close
                </Button>
                <span 
                    style={{ color: 'white', fontWeight: 'bold', fontSize: 18, }}
                >
                    Sort Options
                </span>
            </ModalHeader>
            <ModalBody>
                <div 
                    style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}
                >
                    <div style={{ display: 'flex', gap: 40 }}>
                        <div>
                            <div>
                                <span className="headerText">Sort By:</span>
                            </div>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby={id}
                                    name="sort-by-radio-group"
                                    value={activeTab === 'Tab1' ? sortOrderBy : sortBidBy}
                                    onChange={handleChange}
                                >
                                    <FormControlLabel
                                        value={activeTab === 'Tab1' ? 'id' : 'quoteID'}
                                        control={
                                            <Radio 
                                                sx={{
                                                    color: '#f1b434',
                                                    '&.Mui-checked': {
                                                        color: '#f1b434',
                                                    },
                                                }}
                                            />
                                        }
                                        label={activeTab === 'Tab1' ? 'Order ID' : 'Bid ID'}
                                    />
                                    <FormControlLabel
                                        value={activeTab === 'Tab1' ? 'dateOrder' : 'created'}
                                        control={
                                            <Radio 
                                                sx={{
                                                    color: '#f1b434',
                                                    '&.Mui-checked': {
                                                        color: '#f1b434',
                                                    },
                                                }}
                                            />
                                        }
                                        label={activeTab === 'Tab1' ? 'Order Date' : 'Created Date'}
                                    />
                                    <FormControlLabel
                                        value={activeTab === 'Tab1' ? 'dateRequired' : 'expires'}
                                        control={
                                            <Radio 
                                                sx={{
                                                    color: '#f1b434',
                                                    '&.Mui-checked': {
                                                        color: '#f1b434',
                                                    },
                                                }}
                                            />
                                        }
                                        label={activeTab === 'Tab1' ? 'Required Date' : 'Expire Date'}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>
                        <div>
                            <div>
                                <span className="headerText">Sort Direction:</span>
                            </div>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby={'AscDesc'}
                                    name="ascDesc-radio-group"
                                    value={sortDirection}
                                    onChange={handleDirectionChange}
                                >
                                    <FormControlLabel
                                        value={'asc'}
                                        control={
                                            <Radio 
                                                sx={{
                                                    color: '#f1b434',
                                                    '&.Mui-checked': {
                                                        color: '#f1b434',
                                                    },
                                                }}
                                            />
                                        }
                                        label={'Ascending'}
                                    />
                                    <FormControlLabel
                                        value={'desc'}
                                        control={
                                            <Radio 
                                                sx={{
                                                    color: '#f1b434',
                                                    '&.Mui-checked': {
                                                        color: '#f1b434',
                                                    },
                                                }}
                                            />
                                        }
                                        label={'Descending'}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    </div>
                    <div 
                        className="mt-3" 
                        style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <div>
                            <span className="headerText">Filter Options:</span>
                        </div>
                        <FormGroup
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, max-content)',
                                justifyContent: 'center',
                                columnGap: '20px',
                            }}
                        >
                            {filterOptions.map((item) => (
                                <FormControlLabel
                                    label={item}
                                    control={
                                        <Checkbox
                                            key={item}
                                            checked={filter.includes(item)}
                                            onChange={() => {
                                                if (filter.includes(item)) {
                                                    setFilter((prevItems) => prevItems.filter(option => option !== item));
                                                } else {
                                                    setFilter([...filter, item]);
                                                };
                                            }}
                                            title={item}
                                            sx={{
                                                color: '#f1b434',
                                                '&.Mui-checked': {
                                                    color: '#f1b434',
                                                }
                                            }}
                                        />
                                    }
                                />
                            ))}
                        </FormGroup>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div 
                    style={{ flex: 1, alignItems: 'center', textAlign: 'center' }}
                >
                    <Button
                        className='mainButton fw-bold'
                        onClick={handleSubmit}
                    >
                        Sort
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    )
};

export default SortModal;